import React, { useState } from 'react';
import type { NetworkConnectionState, OnlineGameMode } from '../network/types';

interface OnlineLobbyProps {
  mode: OnlineGameMode;
  networkState: NetworkConnectionState;
  roomCode: string | null;
  errorMessage: string | null;
  onCreateRoom: () => void;
  onJoinRoom: (code: string) => void;
  onCancel: () => void;
}

export const OnlineLobby: React.FC<OnlineLobbyProps> = ({
  mode,
  networkState,
  roomCode,
  errorMessage,
  onCreateRoom,
  onJoinRoom,
  onCancel,
}) => {
  const [tab, setTab] = useState<'create' | 'join'>('create');
  const [inputCode, setInputCode] = useState('');
  const [copied, setCopied] = useState(false);

  const title = mode === 'clash' ? 'Clash 🌐' : mode === 'race' ? 'Race 🌐' : 'Blitz 🌐';
  const subtitle =
    mode === 'clash'
      ? '1v1 Shared Board. Tapping a card locks out your opponent for 5s!'
      : mode === 'race'
      ? 'Same deck seed, no hints. First to finish the deck wins!'
      : '30s start, +10s per set. Sudden death mistake or timeout eliminates you!';

  const handleCopyCode = () => {
    if (!roomCode) return;
    navigator.clipboard?.writeText(roomCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleJoinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputCode.trim().length === 4) {
      onJoinRoom(inputCode.trim());
    }
  };

  const isConnecting = networkState === 'creating_room' || networkState === 'joining_room';
  const isWaiting = networkState === 'waiting_for_guest' && roomCode;

  return (
    <div className="modal-backdrop online-lobby-backdrop">
      <div className="modal-card online-lobby-card">
        <div className="online-lobby-header">
          <h2 className="online-lobby-title">{title}</h2>
          <p className="online-lobby-subtitle">{subtitle}</p>
        </div>

        {!isWaiting && (
          <div className="online-lobby-tabs">
            <button
              className={`lobby-tab-btn ${tab === 'create' ? 'active' : ''}`}
              onClick={() => setTab('create')}
              disabled={isConnecting}
            >
              Host Match
            </button>
            <button
              className={`lobby-tab-btn ${tab === 'join' ? 'active' : ''}`}
              onClick={() => setTab('join')}
              disabled={isConnecting}
            >
              Join Match
            </button>
          </div>
        )}

        <div className="online-lobby-body">
          {tab === 'create' ? (
            <div className="lobby-panel host-panel">
              {isWaiting ? (
                <div className="room-code-display-box">
                  <span className="room-code-label">ROOM CODE</span>
                  <div className="room-code-letters">
                    {roomCode.split('').map((char, i) => (
                      <span key={i} className="code-letter-tile">
                        {char}
                      </span>
                    ))}
                  </div>

                  <div className="lobby-action-row">
                    <button className="btn btn-secondary btn-copy-code" onClick={handleCopyCode}>
                      {copied ? '✓ Copied!' : 'Copy Code'}
                    </button>
                  </div>

                  <div className="waiting-indicator">
                    <div className="spinner-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <p>Waiting for opponent to connect...</p>
                  </div>
                </div>
              ) : (
                <div className="host-init-action">
                  <p className="panel-instruction">
                    Host a game and share the 4-letter room code with a friend.
                  </p>
                  <button
                    className="btn btn-primary btn-lobby-action"
                    id="btn-create-room"
                    onClick={onCreateRoom}
                    disabled={isConnecting}
                  >
                    {isConnecting ? 'Creating Room...' : 'Create Room'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="lobby-panel join-panel">
              <form onSubmit={handleJoinSubmit} className="join-form">
                <label className="join-input-label" htmlFor="room-code-input">
                  Enter 4-Letter Room Code:
                </label>
                <input
                  id="room-code-input"
                  className="room-code-input"
                  type="text"
                  maxLength={4}
                  placeholder="e.g. KMPX"
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  disabled={isConnecting}
                  autoFocus
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-lobby-action"
                  id="btn-join-room-submit"
                  disabled={inputCode.trim().length !== 4 || isConnecting}
                >
                  {isConnecting ? 'Connecting...' : 'Join Match'}
                </button>
              </form>
            </div>
          )}

          {errorMessage && (
            <div className="lobby-error-banner" role="alert">
              ⚠️ {errorMessage}
            </div>
          )}
        </div>

        <div className="online-lobby-footer">
          <button className="btn btn-secondary" id="btn-cancel-lobby" onClick={onCancel} disabled={isConnecting}>
            Back to Menu
          </button>
        </div>
      </div>
    </div>
  );
};

